import cn from "classnames";
import { FormEvent, useEffect, useState, useRef } from "react";
import { DateRangePicker } from "@trussworks/react-uswds";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

// components
import { TrashOutlineIcon } from "components/icons";
import {
  RoundedButton,
  Input,
  Button,
} from "components/LibraryComponents/Components";
import { ResponsiveStack } from "components/ResponsiveStack/ResponsiveStack";
import { Saved } from "components/EditBusinessProfileSections/shared/Saved/Saved";

// utils
import { uuidv4 } from "utils/edit-profile";
import { dateStringFormatter } from "utils/helper";
import { saveErrorToast } from "utils/edit-profile";

// fetch
import { updatePerformanceHistory, getPerformanceHistory } from "data/fetch";

// types
import { SectionsProps } from "pages/BusinessProfileEdit/BusinessProfileEdit";

// hooks 
import { useMultiMeiliTaskToast } from "hooks/useMultiMeiliTaskToast";
import { useSaveConfirmation } from "hooks/useSaveConfirmation";

// styles
import styles from "./PerformanceSection.module.scss";
import { PerformanceHistoryData } from "store/useProfileEditStore";

// mock data
import { MOCK_PERFORMANCE_HISTORY_DATA } from "data/performance-history-mock-data";

export type PerformanceFormData = {
  id: string;
  perf_history_seq?: number; // For tracking existing records for updates/deletes
  name: string;
  contractNumber: string;
  startDate: string;
  endDate: string;
  contractValue: string;
  contactName: string;
  contactPhone: string;
  isNew?: boolean; // To track newly added items vs existing ones
};

const MAX_LIMIT = 75;

function anyUnsavedChanges(
  formData: PerformanceFormData[],
  initialData: PerformanceFormData[]
) {
  return (
    formData.some((item, index) => {
      const initialItem: PerformanceFormData = initialData[index];

      return (
        item.name !== initialItem?.name ||
        item.contractNumber !== initialItem?.contractNumber ||
        item.startDate !== initialItem?.startDate ||
        item.endDate !== initialItem?.endDate ||
        item.contractValue !== initialItem?.contractValue ||
        item.contactName !== initialItem?.contactName ||
        item.contactPhone !== initialItem?.contactPhone
      );
    }) || formData.length !== initialData.length
  );
}

export default function PerformanceSection({
  setUnsavedChanges,
}: SectionsProps) {
  const { uei } = useParams();

  const [formData, setFormData] = useState<PerformanceFormData[]>([]);
  const [initialData, setInitialData] = useState<PerformanceFormData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const PERFORMANCE_HISTORY = "Performance history" as const;

  const { 
    lastSavedSection,
    showConfirmation,
    clearConfirmation
  } = useSaveConfirmation<typeof PERFORMANCE_HISTORY>();

  const addTasks = useMultiMeiliTaskToast();


  // fetch extended profile data
  const { 
    data: performanceHistoryData, 
    isPending, 
    isRefetching 
  } = useQuery<
    PerformanceHistoryData[]
  >({
    queryKey: ["performance-history", uei],

    queryFn: () => getPerformanceHistory(uei),

    gcTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    enabled: Boolean(uei),
    retry: false,

    // TODO: mock initial data
    // initialData: MOCK_PERFORMANCE_HISTORY_DATA,
    // initialDataUpdatedAt: Date.now(),
  });

  const loading = isPending || isRefetching;

  // Initialize form data
  useEffect(() => {
    if (performanceHistoryData) {
      const transformedData = performanceHistoryData
        .filter((item) => !item.deleted_at) // Filter out soft-deleted items
        .map((item) => ({
          id: uuidv4(),
          perf_history_seq: item.perf_history_seq,
          name: item.name || "",
          contractNumber: item.contract || "",
          startDate: item.contract_start_date
            ? item.contract_start_date
            : "",
          endDate: item.contract_end_date
            ? item.contract_end_date
            : "",
          contractValue: item.value || "",
          contactName: item.contract_contact || "",
          contactPhone: item.contract_contact_phone || "",
          isNew: false,
        }));

      setFormData(transformedData);
      setInitialData(transformedData); // Store initial state for change detection
    }
  }, [performanceHistoryData]);

  const unsavedChanges = anyUnsavedChanges(formData, initialData);

  useEffect(() => {
    setUnsavedChanges(unsavedChanges);
    return () => {
      setUnsavedChanges(false);
    };
  }, [unsavedChanges]);

  const noReferences = formData.length === 0;

  const showAddMore = formData.length < MAX_LIMIT;

  function create() {
    if (
      formData.length < MAX_LIMIT &&
      (formData.length === 0 || formRef.current?.reportValidity())
    ) {
      const newFormData: PerformanceFormData = {
        id: uuidv4(),
        name: "",
        contractNumber: "",
        startDate: "",
        endDate: "",
        contractValue: "",
        contactName: "",
        contactPhone: "",
        isNew: true,
      };

      setFormData((prev) => [...prev, newFormData]);
    }
  }

  const onChange = (data: PerformanceFormData) => {
    const updateFormData = formData.map((item) => {
      if (item.id === data.id) {
        return data;
      } else {
        return item;
      }
    });
    clearConfirmation()
    setFormData(updateFormData);
  };

  const onDelete = async (id: string) => {
    const itemToDelete = formData.find((item) => item.id === id);

    if (itemToDelete?.perf_history_seq && !itemToDelete.isNew && uei) {
      // Delete from server if it's an existing item
      setDeletingId(id);
      try {
        const payload = {
          delete: [itemToDelete.perf_history_seq],
        };

        const response = await updatePerformanceHistory(uei, payload);

        const meiliTasks = response?.data?.tasks.map(task => task.taskUid) || [];
        addTasks({
          meiliTasks: meiliTasks,
          label: "Performance history delete",
          id: meiliTasks.join("")
        })
      } catch (error) {
        console.error("Error deleting performance history:", error);
        saveErrorToast();
        setDeletingId(null);
        return; // Don't remove from local state if server delete failed
      }
    }

    // Remove from local state
    const updateFormData = formData.filter((item) => item.id !== id);
    setFormData(updateFormData);
    setInitialData(updateFormData); // Sync initial data after deletion
    setDeletingId(null);
  };

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const submitter = (e.nativeEvent as any).submitter;

    if (submitter && submitter.name === "addAnother" && !unsavedChanges) {
      create()
      return
    }

    if (!form.reportValidity()) {
      console.log("Form is invalid");
      return;
    }

    if (!uei) {
      toast.error("UEI is required to save performance history");
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for API call
      const createEntries = formData
        .filter((item) => item.isNew && item.name && item.id && item.contractValue && item.startDate)
        .map((item) => {
          const entry: any = {
            name: item.name,
            id: item.id, // Include id for tracking
          };
          if (item.startDate) entry.contract_start_date = item.startDate;
          if (item.endDate) entry.contract_end_date = item.endDate;
          if (item.contractValue) entry.value = item.contractValue;
          if (item.contactName) entry.contract_contact = item.contactName;
          if (item.contactPhone) entry.contract_contact_phone = item.contactPhone;
          if (item.contractNumber) entry.contract = item.contractNumber;
          return entry;
        });

      const updateEntries = formData
        .filter(
          (item) =>
            !item.isNew &&
            item.perf_history_seq &&
            item.name &&
            item.contractValue &&
            item.startDate
        )
        .map((item) => ({
          perf_history_seq: item.perf_history_seq,
          name: item.name,
          contract: item.contractNumber,
          contract_start_date: item.startDate ? item.startDate : null,
          contract_end_date: item.endDate ? item.endDate : null,
          value: item.contractValue,
          contract_contact: item.contactName,
          contract_contact_phone: item.contactPhone,
        }));

      const payload: any = {};
      if (createEntries.length > 0) payload.create = createEntries;
      if (updateEntries.length > 0) payload.update = updateEntries;

      if (Object.keys(payload).length === 0) {
        toast.error("No valid entries to save");
        return;
      }

      const response = await updatePerformanceHistory(uei, payload);

      if (response) {

        const meiliTasks = response?.data?.tasks.map(task => task.taskUid) || [];
        addTasks({
          meiliTasks: meiliTasks,
          label: "Performance history edit",
          id: meiliTasks.join("")
        })
        showConfirmation(PERFORMANCE_HISTORY);

        // Update formData to mark items as no longer new and add perf_history_seq for new items
        const newFormData = formData.map((item) => {
          if (item.isNew) {
            const createdEntry = response.data?.created?.find(
              (created: any) =>
                created.id === item.id
            );
            return {
              ...item,
              isNew: false,
              perf_history_seq:
                createdEntry?.perf_history_seq || item.perf_history_seq,
            };
          }

          // return { ...item, isNew: false };
          return item;
        });

        setFormData(newFormData);
        setInitialData(newFormData);
      }
    } catch (error) {
      console.error("Error saving performance history:", error);
      saveErrorToast()
    } finally {
      setIsSubmitting(false);

      if (submitter && submitter.name === "addAnother") {
        create()
      }
    }
  };

  return (
    <div className={styles["performance-section-root"]}>
      <form onSubmit={handleFormSubmit} ref={formRef}>
        <h2 className={styles["heading"]}>Performance history</h2>
        <div className={cn(styles["section-content"], styles["space-y-6"])}>
          <div>
            <h3 className={styles["title"]}>References</h3>
            <p className={styles["desc"]}>
              List contracts with reference contact information to help
              demonstrate what work your small business does.
            </p>
          </div>

          {loading ? (
            <p>Loading performance history...</p>
          ) : (
            <div className={styles["space-y-6"]}>
              {noReferences && (
                <p className={styles["empty-text"]}>
                  No references currently listed.
                </p>
              )}
              {!noReferences &&
                formData.map((item, idx) => (
                  <PerformanceForm
                    key={item.id}
                    id={item.id}
                    number={idx}
                    data={item}
                    onChange={onChange}
                    onDelete={onDelete}
                    isSubmitting={isSubmitting}
                    deletingId={deletingId}
                  />
                ))}
              {showAddMore && (
                <Button
                  type="submit"
                  name="addAnother" 
                  className={styles["outline-btn"]}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {noReferences ? "Add a reference" :
                    unsavedChanges ? "Save and add another" : "Add another"}
                </Button>
              )}
            </div>
          )}

          {!showAddMore && !loading && (
            <p className={styles["perf-limit-text"]}>
              <strong>
                You have entered the maximum number of performance history
                records.
              </strong>{" "}
              Change your certification list above, or remove existing ones
              before adding more.
            </p>
          )}

          {!loading && (
            <ResponsiveStack>
              <Button
                type="submit"
                disabled={isSubmitting || !unsavedChanges}
                loading={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save changes"}
              </Button>
              { lastSavedSection === PERFORMANCE_HISTORY && <Saved />}
            </ResponsiveStack>
          )}
        </div>
      </form>
    </div>
  );
}

type PerformanceFormProps = {
  id: string;
  number: number;
  data: PerformanceFormData;
  onChange: (data: PerformanceFormData) => void;
  onDelete: (id: string) => void;
  deletingId: string | null;
  isSubmitting: boolean;
};

function PerformanceForm(props: PerformanceFormProps) {
  const { number, id, data, onChange, onDelete, isSubmitting, deletingId } = props;

  return (
    <div className={cn(styles["performance-form"], styles["space-y-4"])}>
      <div className={styles["header"]}>
        <h4>Reference #{number + 1}</h4>

        <RoundedButton
          type="button"
          variant="ghost"
          className={styles["icon-btn"]}
          onClick={() => onDelete(data.id)}
          aria-label="Remove reference"
          loading={deletingId === data.id}
          disabled={deletingId !== null || isSubmitting}
        >
          <TrashOutlineIcon aria-hidden={true} />
          Remove
        </RoundedButton>
      </div>

      <div className={styles["form-layout"]}>
        <div className={styles["form-name"]}>
          <Input
            id={`name-${id}`}
            name="name"
            type="text"
            label="Name"
            placeholder="Type the name of the reference here"
            value={data.name}
            suffix={
              <abbr className={styles["required"]}> *</abbr>
            }
            onChange={(e) =>
              onChange({ ...data, name: e.target.value.slice(0, 80) })
            }
            maxLength={80}
            required
          />
        </div>
        <div className={styles["form-contract"]}>
          <Input
            id={`contract-number-${id}`}
            name="contract-number"
            type="text"
            label="Contract number"
            placeholder="Type the contract here"
            value={data.contractNumber}
            onChange={(e) =>
              onChange({ ...data, contractNumber: e.target.value.slice(0, 20) })
            }
            maxLength={20}
          />
        </div>
        <div className={styles["form-value"]}>
          <Input
            id={`contract-value-${id}`}
            name="contract-value"
            type="text"
            label="Contract value"
            suffix={
              <abbr className={styles["required"]}> *</abbr>
            }
            placeholder="Type your contract value here"
            value={data.contractValue}
            onChange={(e) =>
              onChange({ ...data, contractValue: e.target.value.slice(0, 20) })
            }
            maxLength={20}
            required
          />
        </div>
        <div className={styles["form-contact"]}>
          <Input
            id={`contact-name-${id}`}
            name="contact-name"
            type="text"
            label="Contact name"
            placeholder="Type your reference contact here"
            value={data.contactName}
            onChange={(e) =>
              onChange({ ...data, contactName: e.target.value.slice(0, 80) })
            }
            maxLength={80}
          />
        </div>
        <div className={styles["form-phone"]}>
          <Input
            id={`contact-phone-${id}`}
            name="contact-phone"
            type="text"
            label="Contact phone number"
            placeholder="555-555-5555, extension may follow"
            value={data.contactPhone}
            onChange={(e) =>
              onChange({ ...data, contactPhone: e.target.value.slice(0, 80) })
            }
            pattern="^\d{3}-\d{3}-\d{4}.*$"
            maxLength={80}
            title="XXX-XXX-XXX, extension may follow"
          />
        </div>
        <div className={styles["form-date"]}>
          <DateRangePicker
            className={styles["date-range-picker"]}
            startDateLabel="From"
            startDatePickerProps={{
              id: `date-from-${id}`,
              name: "from",
              defaultValue: dateStringFormatter(data.startDate, "YYYY-MM-DD"),
              value: dateStringFormatter(data.startDate, "YYYY-MM-DD"),
              onChange: (val: string) =>
                onChange({
                  ...data,
                  startDate: dateStringFormatter(val, "YYYY-MM-DD"),
                }),
              placeholder: "MM/DD/YYYY",
              pattern: "^(0[1-9]|1[0-2])/(0[1-9]|[12][0-9]|3[01])/\\d{4}$",
              title: "MM/DD/YYYY",
              maxLength: 10,
              required: true
            }}
            endDateLabel="To"
            endDatePickerProps={{
              id: `date-to-${id}`,
              name: "to",
              defaultValue: dateStringFormatter(data.endDate, "YYYY-MM-DD"),
              value: dateStringFormatter(data.endDate, "YYYY-MM-DD"),
              onChange: (val: string) =>
                onChange({
                  ...data,
                  endDate: dateStringFormatter(val, "YYYY-MM-DD"),
                }),
              placeholder: "MM/DD/YYYY",
              pattern: "^(0[1-9]|1[0-2])/(0[1-9]|[12][0-9]|3[01])/\\d{4}$",
              title: "MM/DD/YYYY",
              maxLength: 10,
            }}
          />
        </div>
      </div>
    </div>
  );
}
